import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { apiFetch } from '../jscontrol/apiClient';
import { parseApiList } from '../../utils/productStorefront';
import { useSettings } from '../../context/SettingsContext';
import '../css/Category.css';

const BundleProductRelationManage = forwardRef(({ bundleProductId }, ref) => {
  const { formatPrice } = useSettings();
  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useImperativeHandle(ref, () => ({
    saveTab: () => {
      showToast('Products are saved when you add them from the list.', 'success');
      return true;
    },
  }));

  const fetchRelations = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        pageIndex: '1',
        pageSize: '100',
        bundleProductId: String(bundleProductId),
      });
      const { data } = await apiFetch(`/BundleProductRelation/GetAll?${params}`);
      setRelations(parseApiList(data));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bundleProductId) fetchRelations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleProductId]);

  useEffect(() => {
    if (!showAddModal) return undefined;

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await apiFetch(
          `/Product/Autocomplete?search=${encodeURIComponent(search)}&limit=15`
        );
        const items = Array.isArray(data?.data) ? data.data : [];
        const existing = new Set(relations.map((r) => r.ProductId ?? r.productId));
        setSuggestions(items.filter((p) => !existing.has(p.ProductId ?? p.productId)));
      } catch (_) {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [search, relations, showAddModal]);

  const openAddModal = () => {
    setSearch('');
    setSuggestions([]);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSearch('');
    setSuggestions([]);
  };

  const handleAddProduct = async (product) => {
    setAdding(true);
    try {
      await apiFetch('/BundleProductRelation/Create', {
        method: 'POST',
        data: {
          BundleProductId: Number(bundleProductId),
          ProductId: product.ProductId,
        },
      });
      showToast(`"${product.ProductName}" added to bundle successfully.`);
      closeAddModal();
      fetchRelations();
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (relation) => {
    if (!window.confirm('Remove this product from bundle?')) return;
    setDeleting(relation.BundleProductRelationId ?? relation.bundleProductRelationId);
    try {
      await apiFetch(`/BundleProductRelation/Delete/${relation.BundleProductRelationId ?? relation.bundleProductRelationId}`, {
        method: 'DELETE',
      });
      showToast('Product removed from bundle.');
      fetchRelations();
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {toast && (
        <div
          className={`alert alert-${toast.type} py-2 px-3`}
          style={{ fontSize: '0.83rem', position: 'absolute', top: 0, right: 0, zIndex: 10, minWidth: 220 }}
        >
          {toast.msg}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-semibold">Products in Bundle</h6>
        <button type="button" className="cat-add-btn" onClick={openAddModal}>
          <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i>
          Add
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{error}</div>
      ) : relations.length === 0 ? (
        <div className="text-center text-muted py-4" style={{ fontSize: '0.9rem' }}>
          No products in this bundle yet. Click <strong>Add</strong> to search and include products.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>SKU</th>
                <th>Price</th>
                <th>Added On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {relations.map((r) => {
                const relationId = r.BundleProductRelationId ?? r.bundleProductRelationId;
                const productName = r.ProductName ?? r.productName;
                const sku = r.SKU ?? r.sku;
                const price = r.Price ?? r.price;
                const addedOn = r.AddedOn ?? r.addedOn;
                return (
                <tr key={relationId}>
                  <td>{productName}</td>
                  <td>{sku || '—'}</td>
                  <td>{price != null ? formatPrice(price) : '—'}</td>
                  <td>{addedOn ? new Date(addedOn).toLocaleString() : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDelete(r)}
                      disabled={deleting === relationId}
                    >
                      {deleting === relationId ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <i className="mdi mdi-delete" />
                      )}
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div
          className="cat-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeAddModal(); }}
        >
          <div className="cat-modal" style={{ maxWidth: 520 }}>
            <div className="cat-modal-header">
              <h5>Add Product to Bundle</h5>
              <button type="button" className="cat-modal-close" onClick={closeAddModal}>&times;</button>
            </div>

            <div className="cat-modal-body">
              <div className="form-group mb-3">
                <label className="form-label">Search product</label>
                <div className="cat-filter-wrap">
                  <i className="mdi mdi-magnify cat-filter-icon"></i>
                  <input
                    type="text"
                    className="cat-filter-input w-100"
                    placeholder="Type product name or SKU..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <div
                className="border rounded"
                style={{ maxHeight: 300, overflowY: 'auto', background: '#fff' }}
              >
                {searching ? (
                  <div className="text-muted p-3 text-center" style={{ fontSize: '0.85rem' }}>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Searching products...
                  </div>
                ) : suggestions.length === 0 ? (
                  <div className="text-muted p-3 text-center" style={{ fontSize: '0.85rem' }}>
                    {search.trim()
                      ? 'No matching products found.'
                      : 'Start typing to search products.'}
                  </div>
                ) : (
                  suggestions.map((p) => (
                    <button
                      key={p.ProductId}
                      type="button"
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center border-0 border-bottom w-100 text-start"
                      onClick={() => handleAddProduct(p)}
                      disabled={adding}
                      style={{ padding: '12px 14px' }}
                    >
                      <span>{p.ProductName}</span>
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                        {p.SKU ? `${p.SKU} · ` : ''}
                        {p.Price != null ? formatPrice(p.Price) : ''}
                      </span>
                    </button>
                  ))
                )}
              </div>

              <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                Click a product to add it to this bundle.
              </p>
            </div>

            <div className="cat-modal-footer">
              <button type="button" className="btn btn-light" onClick={closeAddModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default BundleProductRelationManage;
